"use client";

import { c as _c } from "react/compiler-runtime";
import { Locale } from "@/types/locale";
import React, { useCallback, useMemo, useState } from "react";
import { Button } from "@components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover-card";
import { useAnyTranslations } from "@lib/i18n/utils";
import { cn } from "@lib/utils";
import { ProgramProposalEntity } from "@features/events-program/types/program-proposal";
import { AssignmentMemberDisplayLookup } from "@features/events-program/ui/assigments/assignment-member-display";
import { buildProposalVoteDisplays, buildProposalVoteResolutionSummary, ProposalVoteDisplay } from "./proposal-vote-display";
import { ProposalVotesSummaryContent, ProposalVotesSummaryLabels } from "./proposal-votes-summary-content";
import { ProposalVotesHiddenContent, ProposalVotesHiddenContentLabels } from "./proposal-votes-hidden-content";
type ProposalVotesHoverCardProps = {
  proposal: ProgramProposalEntity;
  locale: Locale;
  voteDisplayLookup?: AssignmentMemberDisplayLookup;
  currentTeamMemberIds?: readonly number[];
  trigger: React.ReactElement<{
    onClick?: React.MouseEventHandler;
  }>;
  contentClassName?: string;
  detailsVisibility?: ProposalVoteDetailsVisibility;
  hiddenLabels?: ProposalVotesHiddenContentLabels;
  voteActions?: ProposalVoteActions;
  voteAction?: ProposalVoteAction;
  onEditVote?: (voterId: number) => void;
  canEditVote?: (vote: ProposalVoteDisplay) => boolean;
  averageRatingText?: string;
  onOpenProposal?: () => void;
};
const hoverCardContentMaxHeight = "min(32rem, var(--radix-hover-card-content-available-height, 80vh))";
export type ProposalVoteAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "outline";
};
export type ProposalVoteActions = {
  primary: ProposalVoteAction;
  secondary?: ProposalVoteAction;
};
export type ProposalVoteDetailsVisibility = {
  detailsVisible: boolean;
  ratingHidden: boolean;
  canRevealSingleProposal: boolean;
  onRevealSingleProposal?: () => void;
};
const buildSummaryLabels = (tVoteSummary: ReturnType<typeof useAnyTranslations>): ProposalVotesSummaryLabels => ({
  title: tVoteSummary("title"),
  votersCount: count => tVoteSummary("votersCount", {
    count
  }),
  detailsTitle: tVoteSummary("detailsTitle"),
  emptySummary: tVoteSummary("emptySummary"),
  emptyComment: tVoteSummary("emptyComment"),
  scoreLabel: tVoteSummary("scoreLabel"),
  openProposal: tVoteSummary("openProposal"),
  proxyVoteBadge: tVoteSummary("proxyVoteBadge"),
  myVoteBadge: tVoteSummary("myVoteBadge"),
  averageRatingLabel: tVoteSummary("averageRatingLabel"),
  editVoteAction: tVoteSummary("editVoteAction"),
  needsReview: tVoteSummary("needsReview")
});
type ProposalVotesHoverCardContentProps = {
  proposal: ProgramProposalEntity;
  locale: Locale;
  voteDisplayLookup?: AssignmentMemberDisplayLookup;
  currentTeamMemberIds?: readonly number[];
  contentClassName?: string;
  detailsVisibility?: ProposalVoteDetailsVisibility;
  hiddenLabels?: ProposalVotesHiddenContentLabels;
  voteActions?: ProposalVoteActions;
  voteAction?: ProposalVoteAction;
  onEditVote?: (voterId: number) => void;
  canEditVote?: (vote: ProposalVoteDisplay) => boolean;
  averageRatingText?: string;
  onOpenProposal?: () => void;
};
const ProposalVotesHoverCardActions = t0 => {
  const $ = _c(13);
  const {
    voteActions,
    voteAction
  } = t0;
  let t1;
  if ($[0] !== voteAction || $[1] !== voteActions) {
    t1 = voteActions ?? (voteAction ? {
      primary: voteAction
    } : undefined);
    $[0] = voteAction;
    $[1] = voteActions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const actions = t1;
  if (!actions) {
    return null;
  }
  const t2 = actions.primary.variant ?? "default";
  let t3;
  if ($[3] !== actions.primary.disabled || $[4] !== actions.primary.label || $[5] !== actions.primary.onClick || $[6] !== t2) {
    t3 = <Button type="button" size="sm" className="w-full" variant={t2} disabled={actions.primary.disabled} onClick={actions.primary.onClick} data-testid="proposal-vote-hover-primary-action">{actions.primary.label}</Button>;
    $[3] = actions.primary.disabled;
    $[4] = actions.primary.label;
    $[5] = actions.primary.onClick;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== actions.secondary) {
    t4 = actions.secondary ? <Button type="button" size="sm" className="w-full" variant={actions.secondary.variant ?? "outline"} disabled={actions.secondary.disabled} onClick={actions.secondary.onClick} data-testid="proposal-vote-hover-secondary-action">{actions.secondary.label}</Button> : null;
    $[8] = actions.secondary;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex shrink-0 flex-col gap-2 border-t bg-popover p-3">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const ProposalVotesHoverCardContent = React.memo<ProposalVotesHoverCardContentProps>(t0 => {
  const $ = _c(44);
  const {
    proposal,
    locale,
    voteDisplayLookup,
    currentTeamMemberIds,
    contentClassName,
    detailsVisibility,
    hiddenLabels,
    voteActions,
    voteAction,
    onEditVote,
    canEditVote,
    averageRatingText,
    onOpenProposal
  } = t0;
  const tVoteSummary = useAnyTranslations("features.events-program.ui.tables.eventVotingPanelTable.voteSummary");
  const tAny = useAnyTranslations("features.events-program.ui.tables.eventVotingPanelTable");
  let t1;
  if ($[0] !== hiddenLabels || $[1] !== tAny) {
    t1 = hiddenLabels ?? {
      title: tAny("voteVisibility.hidden.title"),
      description: tAny("voteVisibility.hidden.description"),
      revealSingleProposal: tAny("voteVisibility.hidden.revealSingleProposal")
    };
    $[0] = hiddenLabels;
    $[1] = tAny;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hiddenStateLabels = t1;
  let t2;
  if ($[3] !== tVoteSummary) {
    t2 = buildSummaryLabels(tVoteSummary);
    $[3] = tVoteSummary;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const labels = t2;
  let t3;
  if ($[5] !== currentTeamMemberIds || $[6] !== detailsVisibility?.detailsVisible || $[7] !== locale || $[8] !== proposal || $[9] !== tVoteSummary || $[10] !== voteDisplayLookup) {
    t3 = () => detailsVisibility?.detailsVisible === false ? [] : buildProposalVoteDisplays(proposal, {
      locale,
      tVoteSummary,
      voteDisplayLookup,
      currentTeamMemberIds
    });
    $[5] = currentTeamMemberIds;
    $[6] = detailsVisibility?.detailsVisible;
    $[7] = locale;
    $[8] = proposal;
    $[9] = tVoteSummary;
    $[10] = voteDisplayLookup;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  detailsVisibility?.detailsVisible;
  let t4;
  if ($[12] !== t3) {
    t4 = t3();
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const votes = t4;
  let t5;
  if ($[14] !== votes) {
    t5 = buildProposalVoteResolutionSummary(votes);
    $[14] = votes;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const summary = t5;
  if (detailsVisibility?.detailsVisible === false) {
    let t6;
    if ($[16] !== contentClassName || $[17] !== detailsVisibility.canRevealSingleProposal || $[18] !== detailsVisibility.onRevealSingleProposal || $[19] !== hiddenStateLabels) {
      t6 = <div className="min-h-0 overflow-y-auto overscroll-contain" data-testid="proposal-votes-scroll-region"><ProposalVotesHiddenContent labels={hiddenStateLabels} canRevealSingleProposal={detailsVisibility.canRevealSingleProposal} onRevealSingleProposal={detailsVisibility.onRevealSingleProposal} className={contentClassName} /></div>;
      $[16] = contentClassName;
      $[17] = detailsVisibility.canRevealSingleProposal;
      $[18] = detailsVisibility.onRevealSingleProposal;
      $[19] = hiddenStateLabels;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== voteAction || $[22] !== voteActions) {
      t7 = <ProposalVotesHoverCardActions voteActions={voteActions} voteAction={voteAction} />;
      $[21] = voteAction;
      $[22] = voteActions;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] !== t6 || $[25] !== t7) {
      t8 = <div className="flex min-h-0 flex-col">{t6}{t7}</div>;
      $[24] = t6;
      $[25] = t7;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    return t8;
  }
  let t6;
  if ($[27] !== contentClassName) {
    t6 = cn("w-[min(24rem,calc(100vw-2rem))]", contentClassName);
    $[27] = contentClassName;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== averageRatingText || $[30] !== canEditVote || $[31] !== labels || $[32] !== onEditVote || $[33] !== onOpenProposal || $[34] !== summary || $[35] !== t6 || $[36] !== votes) {
    t7 = <div className="min-h-0 overflow-y-auto overscroll-contain" data-testid="proposal-votes-scroll-region"><ProposalVotesSummaryContent votes={votes} summary={summary} labels={labels} className={t6} onOpenProposal={onOpenProposal} averageRatingText={averageRatingText} onEditVote={onEditVote} canEditVote={canEditVote} /></div>;
    $[29] = averageRatingText;
    $[30] = canEditVote;
    $[31] = labels;
    $[32] = onEditVote;
    $[33] = onOpenProposal;
    $[34] = summary;
    $[35] = t6;
    $[36] = votes;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== voteAction || $[39] !== voteActions) {
    t8 = <ProposalVotesHoverCardActions voteActions={voteActions} voteAction={voteAction} />;
    $[38] = voteAction;
    $[39] = voteActions;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t7 || $[42] !== t8) {
    t9 = <div className="flex min-h-0 flex-col">{t7}{t8}</div>;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
});
ProposalVotesHoverCardContent.displayName = "ProposalVotesHoverCardContent";
export const ProposalVotesHoverCard = React.memo<ProposalVotesHoverCardProps>(t0 => {
  const $ = _c(28);
  const {
    proposal,
    locale,
    voteDisplayLookup,
    currentTeamMemberIds,
    trigger,
    contentClassName,
    detailsVisibility,
    hiddenLabels,
    voteActions,
    voteAction,
    onEditVote,
    canEditVote,
    averageRatingText,
    onOpenProposal
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== onOpenProposal) {
    t1 = () => {
      setOpen(false);
      onOpenProposal?.();
    };
    $[0] = onOpenProposal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOpenProposal = t1;
  let t2;
  if ($[2] !== trigger) {
    const t3 = event => {
      setOpen(false);
      trigger.props.onClick?.(event);
    };
    let t4;
    if ($[4] !== t3) {
      t4 = {
        onClick: t3
      };
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t2 = React.cloneElement(trigger, t4);
    $[2] = trigger;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const triggerWithDismiss = t2;
  let t3;
  if ($[6] !== triggerWithDismiss) {
    t3 = <HoverCardTrigger asChild={true}>{triggerWithDismiss}</HoverCardTrigger>;
    $[6] = triggerWithDismiss;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== averageRatingText || $[9] !== canEditVote || $[10] !== contentClassName || $[11] !== currentTeamMemberIds || $[12] !== detailsVisibility || $[13] !== handleOpenProposal || $[14] !== hiddenLabels || $[15] !== locale || $[16] !== onEditVote || $[17] !== onOpenProposal || $[18] !== open || $[19] !== proposal || $[20] !== voteAction || $[21] !== voteActions || $[22] !== voteDisplayLookup) {
    t4 = open ? <HoverCardContent className="flex w-auto max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0" align="end" style={{
      maxHeight: hoverCardContentMaxHeight
    }}><ProposalVotesHoverCardContent proposal={proposal} locale={locale} voteDisplayLookup={voteDisplayLookup} currentTeamMemberIds={currentTeamMemberIds} contentClassName={contentClassName} detailsVisibility={detailsVisibility} hiddenLabels={hiddenLabels} voteActions={voteActions} voteAction={voteAction} onEditVote={onEditVote} canEditVote={canEditVote} averageRatingText={averageRatingText} onOpenProposal={onOpenProposal ? handleOpenProposal : undefined} /></HoverCardContent> : null;
    $[8] = averageRatingText;
    $[9] = canEditVote;
    $[10] = contentClassName;
    $[11] = currentTeamMemberIds;
    $[12] = detailsVisibility;
    $[13] = handleOpenProposal;
    $[14] = hiddenLabels;
    $[15] = locale;
    $[16] = onEditVote;
    $[17] = onOpenProposal;
    $[18] = open;
    $[19] = proposal;
    $[20] = voteAction;
    $[21] = voteActions;
    $[22] = voteDisplayLookup;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== open || $[25] !== t3 || $[26] !== t4) {
    t5 = <HoverCard open={open} onOpenChange={setOpen}>{t3}{t4}</HoverCard>;
    $[24] = open;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
});
ProposalVotesHoverCard.displayName = "ProposalVotesHoverCard";